import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  User,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  AlertTriangle,
  UserPlus,
  RefreshCw,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Shield,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  FileText,
  Download,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { usersAPI } from "../../services/api";
import { usePermissions } from "../../utils/permissionUtils";
import { toast } from "sonner";

// Import modal components
import DeleteUserModal from "../../components/modal/admin/DeleteUserModal";
import EditUserModal from "../../components/modal/admin/EditUserModal";
import UserDetailsModal from "../../components/modal/admin/UserDetailsModal";
import ReviewRegistrationModal from "../../components/modal/admin/ReviewRegistrationModal";
import ApprovalRequestModal from "../../components/modal/admin/ApprovalRequestModal";

// Import tab components
import PendingApprovalsTab from "../../components/admin/PendingApprovalsTab";
import ApprovalHistoryTab from "../../components/admin/ApprovalHistoryTab";

const UserManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Approval system state
  const [activeTab, setActiveTab] = useState("users");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalActionType, setApprovalActionType] = useState("");
  const [selectedUserForApproval, setSelectedUserForApproval] = useState(null);

  // Pagination state for each table
  const [clientUsersPage, setClientUsersPage] = useState(1);
  const [adminUsersPage, setAdminUsersPage] = useState(1);
  const [usersPerPage] = useState(5);

  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset pagination when filters change
  React.useEffect(() => {
    setClientUsersPage(1);
    setAdminUsersPage(1);
  }, [debouncedSearchTerm, filterRole, filterStatus]);

  // Fetch client users (investor, custodian)
  const { data: clientUsersData, isPending: clientUsersLoading } = useQuery({
    queryKey: [
      "clientUsers",
      debouncedSearchTerm,
      filterRole,
      filterStatus,
      clientUsersPage,
    ],
    queryFn: async () => {
      // If filtering by a role that's not client-related, return empty
      if (
        filterRole !== "all" &&
        !["investor", "custodian"].includes(filterRole)
      ) {
        return {
          users: [],
          pagination: {
            totalPages: 0,
            totalItems: 0,
            currentPage: 1,
            itemsPerPage: usersPerPage,
          },
        };
      }

      // Get all users and filter client users on the frontend
      const response = await usersAPI.getUsers({
        search: debouncedSearchTerm || undefined,
        role: filterRole === "all" ? undefined : filterRole,
        status: filterStatus === "all" ? undefined : filterStatus,
        limit: 1000, // Get more users to filter properly
      });

      // Extract users from the correct response structure
      const allUsers = response?.data?.data?.users || [];
      const clientUsers = allUsers.filter((user) =>
        ["investor", "custodian"].includes(user.role)
      );

      // Sort users: pending verification first, then by creation date (newest first)
      const sortedClientUsers = clientUsers.sort((a, b) => {
        // First priority: pending verification users
        if (
          a.registrationStatus === "pending_verification" &&
          b.registrationStatus !== "pending_verification"
        ) {
          return -1;
        }
        if (
          a.registrationStatus !== "pending_verification" &&
          b.registrationStatus === "pending_verification"
        ) {
          return 1;
        }
        // Second priority: creation date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      // Implement client-side pagination
      const startIndex = (clientUsersPage - 1) * usersPerPage;
      const endIndex = startIndex + usersPerPage;
      const paginatedUsers = sortedClientUsers.slice(startIndex, endIndex);

      return {
        users: paginatedUsers,
        pagination: {
          currentPage: clientUsersPage,
          totalPages: Math.ceil(clientUsers.length / usersPerPage),
          totalItems: clientUsers.length,
          itemsPerPage: usersPerPage,
        },
      };
    },
    select: (data) => data || { users: [], pagination: {} },
  });

  // Fetch admin users (super_admin, issuer, facility_agent)
  const { data: adminUsersData, isPending: adminUsersLoading } = useQuery({
    queryKey: [
      "adminUsers",
      debouncedSearchTerm,
      filterRole,
      filterStatus,
      adminUsersPage,
    ],
    queryFn: async () => {
      // If filtering by a role that's not admin-related, return empty
      if (
        filterRole !== "all" &&
        ![
          "super_admin",
          "issuer",
          "facility_agent",
          "primary_subscriber",
          "trustee",
        ].includes(filterRole)
      ) {
        return {
          users: [],
          pagination: {
            totalPages: 0,
            totalItems: 0,
            currentPage: 1,
            itemsPerPage: usersPerPage,
          },
        };
      }

      // Get all users and filter admin users on the frontend
      const response = await usersAPI.getUsers({
        search: debouncedSearchTerm || undefined,
        role: filterRole === "all" ? undefined : filterRole,
        status: filterStatus === "all" ? undefined : filterStatus,
        limit: 1000, // Get more users to filter properly
      });

      // Extract users from the correct response structure
      const allUsers = response?.data?.data?.users || [];
      const adminUsers = allUsers.filter((user) =>
        [
          "super_admin",
          "issuer",
          "facility_agent",
          "primary_subscriber",
          "trustee",
        ].includes(user.role)
      );

      // Sort users: pending verification first, then by creation date (newest first)
      const sortedAdminUsers = adminUsers.sort((a, b) => {
        // First priority: pending verification users
        if (
          a.registrationStatus === "pending_verification" &&
          b.registrationStatus !== "pending_verification"
        ) {
          return -1;
        }
        if (
          a.registrationStatus !== "pending_verification" &&
          b.registrationStatus === "pending_verification"
        ) {
          return 1;
        }
        // Second priority: creation date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      // Implement client-side pagination
      const startIndex = (adminUsersPage - 1) * usersPerPage;
      const endIndex = startIndex + usersPerPage;
      const paginatedUsers = sortedAdminUsers.slice(startIndex, endIndex);

      return {
        users: paginatedUsers,
        pagination: {
          currentPage: adminUsersPage,
          totalPages: Math.ceil(adminUsers.length / usersPerPage),
          totalItems: adminUsers.length,
          itemsPerPage: usersPerPage,
        },
      };
    },
    select: (data) => data || { users: [], pagination: {} },
  });

  // Fetch all users for statistics (without pagination)
  const { data: allUsersData } = useQuery({
    queryKey: ["allUsersStats", debouncedSearchTerm, filterRole, filterStatus],
    queryFn: async () => {
      const response = await usersAPI.getUsers({
        search: debouncedSearchTerm || undefined,
        role: filterRole === "all" ? undefined : filterRole,
        status: filterStatus === "all" ? undefined : filterStatus,
        limit: 1000, // Get a large number for statistics
      });
      return response?.data?.data?.users || [];
    },
    select: (data) => data || [],
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => usersAPI.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
      setShowDeleteModal(false);
      setSelectedUser(null);
    },
  });

  const activateUserMutation = useMutation({
    mutationFn: (userId) => usersAPI.activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
      toast.success("User activated successfully");
      setShowEditModal(false);
      setSelectedUser(null);
    },
  });

  const deactivateUserMutation = useMutation({
    mutationFn: (userId) => usersAPI.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
      toast.success("User deactivated successfully");
      setShowEditModal(false);
      setSelectedUser(null);
    },
  });

  const approveUserMutation = useMutation({
    mutationFn: (userId) => usersAPI.approveUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
      toast.success("User registration approved successfully");
    },
  });

  const rejectUserMutation = useMutation({
    mutationFn: ({ userId, reason }) => usersAPI.rejectUser(userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
      toast.success("User registration rejected successfully");
    },
  });

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Access Denied
          </h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  const roles = [
    { value: "issuer", label: "Issuer" },
    { value: "facility_agent", label: "Facility Agent" },
    { value: "custodian", label: "Custodian" },
    { value: "super_admin", label: "Super Admin" },
    { value: "investor", label: "Investor" },
    { value: "primary_subscriber", label: "Primary Subscriber" },
    { value: "trustee", label: "Trustee" },
  ];

  const statuses = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Utility function to get user display name
  const getUserDisplayName = (user) => {
    if (user.personalInfo?.firstName && user.personalInfo?.lastName) {
      return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
    }
    if (user.personalInfo?.fullName) {
      return user.personalInfo.fullName;
    }
    if (user.corporateInfo?.companyName) {
      return user.corporateInfo.companyName;
    }
    if (user.corporateInfo?.primaryAccountHolderName) {
      return user.corporateInfo.primaryAccountHolderName;
    }
    return user.email.split("@")[0];
  };

  const getUserCompany = (user) => {
    if (user.corporateInfo?.companyName) {
      return user.corporateInfo.companyName;
    }
    return "";
  };

  // Utility function to get friendly document names
  const getDocumentDisplayName = (type) => {
    const docNames = {
      // KYC Documents
      identityDocument: "Identity Document",
      proofOfAddress: "Proof of Address",
      selfieWithId: "Selfie with ID",
      incomeProof: "Income Proof",
      // KYB Documents
      ssmProfile: "SSM Profile",
      memorandumArticles: "Memorandum & Articles",
      boardResolution: "Board Resolution",
      financialStatements: "Financial Statements",
      // Membership Documents
      participationCertificate:
        "Participation Certificate / Confirmation of Membership",
      officialMemberRegistry: "Official Member Registry Proof",
      operationalProcedures:
        "Operational Procedures / Rulebook Acknowledgement",
      authorizedSignatories: "Authorized Signatories & User Access Forms",
      technicalConnectivity: "Technical Connectivity Evidence",
      feePaymentReceipts: "Fee Payment Receipts",
      formalMembershipApproval: "Formal Membership Approval Letter",
    };
    return docNames[type] || type.replace(/([A-Z])/g, " $1").trim();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRoleBadge = (role) => {
    const roleNames = {
      issuer: "Issuer",
      facility_agent: "Facility Agent",
      custodian: "Custodian",
      super_admin: "Super Admin",
      investor: "Investor",
      primary_subscriber: "Primary Subscriber",
      trustee: "Trustee",
    };
    return roleNames[role] || role;
  };

  const getStatusBadge = (user) => {
    const status = user.isActive ? "active" : "inactive";
    const statusConfig = {
      active: { label: "Active", class: "badge-success" },
      inactive: { label: "Inactive", class: "badge-secondary" },
    };
    const config = statusConfig[status] || {
      label: status,
      class: "badge-secondary",
    };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const getRegistrationStatusBadge = (registrationStatus) => {
    const statusConfig = {
      verified: { label: "Verified", class: "badge-success" },
      pending_verification: {
        label: "Pending Verification",
        class: "badge-warning",
      },
      rejected: { label: "Rejected", class: "badge-error" },
    };
    const config = statusConfig[registrationStatus] || {
      label: registrationStatus,
      class: "badge-secondary",
    };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  // This filtering is now handled by the API calls, so we can remove this section

  const handleUserAction = (action, user) => {
    setDropdownOpen(null);
    setSelectedUser(user);

    switch (action) {
      case "edit":
        setShowEditModal(true);
        break;
      case "delete":
        setShowDeleteModal(true);
        break;
      case "approve":
        approveUserMutation.mutate(user._id);
        break;
      case "reject":
        rejectUserMutation.mutate({ userId: user._id, reason: "" });
        break;
      case "review":
        setShowReviewModal(true);
        break;
      case "view_details":
        setShowDetailsModal(true);
        break;
      default:
        break;
    }
  };

  // Handle approval request submission
  const handleApprovalRequest = (actionType, user) => {
    setApprovalActionType(actionType);
    setSelectedUserForApproval(user);
    setShowApprovalModal(true);
  };

  // Handle approval request success
  const handleApprovalRequestSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["clientUsers"] });
    queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    queryClient.invalidateQueries({ queryKey: ["allUsersStats"] });
  };

  // Extract users from API responses
  const clientUsers = clientUsersData?.users || [];
  const adminUsers = adminUsersData?.users || [];
  const allUsers = allUsersData || [];

  const renderUserTable = (
    users,
    title,
    pagination,
    currentPage,
    onPageChange
  ) => (
    <div className="card">
      <div className="card-header">
        <h3>{title}</h3>
      </div>
      <div className="p-0 card-body">
        {users.length === 0 && !debouncedSearchTerm ? (
          <div className="p-6 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-secondary-400" />
            <p className="text-secondary-500">
              No users found for this category.
            </p>
          </div>
        ) : users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell">User ID</th>
                    <th className="table-header-cell">Role</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Registration</th>
                    <th className="table-header-cell">Approval Status</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-light-gray">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
                              <span className="text-sm font-medium text-primary-600">
                                {getUserDisplayName(user)
                                  .charAt(0)
                                  .toUpperCase()}
                                {getUserDisplayName(user)
                                  .split(" ")[1]
                                  ?.charAt(0)
                                  ?.toUpperCase() || ""}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-secondary-900">
                              {getUserDisplayName(user)}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {user.email}
                            </div>
                            {getUserCompany(user) && (
                              <div className="text-xs text-dark-gray">
                                {getUserCompany(user)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono text-sm text-secondary-900">
                          {user._id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="badge badge-info">
                          {getRoleBadge(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRegistrationStatusBadge(user.registrationStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.approvalStatus ? (
                          <span
                            className={`badge ${
                              user.approvalStatus === "approved"
                                ? "badge-success"
                                : user.approvalStatus === "rejected"
                                ? "badge-error"
                                : user.approvalStatus === "pending_l2"
                                ? "badge-warning"
                                : "badge-secondary"
                            }`}
                          >
                            {user.approvalStatus === "approved"
                              ? "Approved"
                              : user.approvalStatus === "rejected"
                              ? "Rejected"
                              : user.approvalStatus === "pending_l2"
                              ? "Pending L2"
                              : user.approvalStatus === "pending_l1"
                              ? "Pending L1"
                              : user.approvalStatus}
                          </span>
                        ) : (
                          <span className="badge badge-secondary">
                            No Action
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-secondary-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <div className="relative inline-block text-left">
                          <button
                            onClick={() =>
                              setDropdownOpen(
                                dropdownOpen === user._id ? null : user._id
                              )
                            }
                            className="p-2 rounded-md hover:bg-secondary-100 focus:outline-none"
                          >
                            <MoreHorizontal className="w-4 h-4 text-secondary-500" />
                          </button>
                          {dropdownOpen === user._id && (
                            <div className="absolute right-0 z-10 w-56 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <div className="py-1">
                                {user.registrationStatus ===
                                "pending_verification" ? (
                                  // Actions for pending verification users
                                  <>
                                    <button
                                      onClick={() =>
                                        handleUserAction("approve", user)
                                      }
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-secondary-700 hover:bg-secondary-100"
                                    >
                                      <CheckCircle className="w-4 h-4 mr-2 text-success-600" />
                                      Approve
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleUserAction("reject", user)
                                      }
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-secondary-700 hover:bg-secondary-100"
                                    >
                                      <XCircle className="w-4 h-4 mr-2 text-error-600" />
                                      Reject
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleUserAction("review", user)
                                      }
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-secondary-700 hover:bg-secondary-100"
                                    >
                                      <Eye className="w-4 h-4 mr-2 text-primary-600" />
                                      Review
                                    </button>
                                  </>
                                ) : (
                                  // Actions for verified/rejected users
                                  <>
                                    <button
                                      onClick={() =>
                                        handleUserAction("view_details", user)
                                      }
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-dark-gray hover:bg-secondary-100"
                                    >
                                      <Eye className="w-4 h-4 mr-2 text-primary" />
                                      View Details
                                    </button>
                                                                    <button
                                      onClick={() =>
                                        handleUserAction("edit", user)
                                      }
                                      
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-dark-gray hover:bg-secondary-100"
                                    >
                                      <Edit className="w-4 h-4 mr-2 text-primary" />
                                      Edit User
                                    </button>

                                    <div className="my-1 border-t border-secondary-100"></div>
                                    <button
                                      onClick={() =>
                                        handleApprovalRequest("delete", user)
                                      }
                                      
                                      className="flex items-center w-full px-4 py-2 text-sm text-left text-dark-gray hover:bg-error-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2 text-error-600" />
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-6 py-3 border-t bg-secondary-50 border-light-gray">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-secondary-500">
                    <span>
                      Showing {(currentPage - 1) * pagination.itemsPerPage + 1}{" "}
                      to{" "}
                      {Math.min(
                        currentPage * pagination.itemsPerPage,
                        pagination.totalItems
                      )}{" "}
                      of {pagination.totalItems} results
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onPageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    {Array.from(
                      { length: pagination.totalPages },
                      (_, i) => i + 1
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          page === currentPage
                            ? "bg-primary-600 text-white"
                            : "bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => onPageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                User Management
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage user accounts for platform onboarding
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                  onClick={() => navigate("/admin/users/create")}
                  
                  className="btn-primary"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* User Statistics */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-4">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                  <Users className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold">{allUsers.length}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                  <CheckCircle className="w-6 h-6 text-success-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Active
                  </p>
                  <p className="text-2xl font-bold">
                    {
                      allUsers.filter(
                        (u) =>
                          u.isActive &&
                          u.registrationStatus !== "pending_verification"
                      ).length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-warning-100">
                  <AlertTriangle className="w-6 h-6 text-warning-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Pending
                  </p>
                  <p className="text-2xl font-bold">
                    {
                      allUsers.filter(
                        (u) => u.registrationStatus === "pending_verification"
                      ).length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-error-100">
                  <XCircle className="w-6 h-6 text-error-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Inactive
                  </p>
                  <p className="text-2xl font-bold">
                    {
                      allUsers.filter(
                        (u) =>
                          !u.isActive &&
                          u.registrationStatus !== "pending_verification"
                      ).length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            <button
              onClick={() => setActiveTab("users")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "users"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab("pending-approvals")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "pending-approvals"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Pending Approvals
            </button>
            <button
              onClick={() => setActiveTab("approval-history")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "approval-history"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Approval History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "users" && (
          <>
            {/* Filters and Search */}
            <div className="mb-6 border card border-light-gray">
              <div className="card-body">
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute w-4 h-4 left-3 top-3 text-dark-gray" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 form-input"
                      />
                    </div>
                  </div>
                  <div className="lg:w-48">
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="form-input"
                    >
                      <option value="all">All Roles</option>
                      {roles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="lg:w-48">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="form-input"
                    >
                      <option value="all">All Status</option>
                      {statuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Tables in Single Column */}
            <div className="space-y-6">
              {/* Investor (Direct) and Custodian Users */}
              {renderUserTable(
                clientUsers,
                "Investor (Direct) & Custodian Users",
                clientUsersData?.pagination,
                clientUsersPage,
                setClientUsersPage
              )}

              {/* Super Admin, Issuer, Facility Agent, Primary Subscriber & Trustee Users */}
              {renderUserTable(
                adminUsers,
                "Super Admin, Issuer, Facility Agent, Primary Subscriber & Trustee Users",
                adminUsersData?.pagination,
                adminUsersPage,
                setAdminUsersPage
              )}
            </div>
          </>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === "pending-approvals" && <PendingApprovalsTab />}

        {/* Approval History Tab */}
        {activeTab === "approval-history" && <ApprovalHistoryTab />}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        onConfirm={deleteUserMutation.mutate}
        isDeleting={deleteUserMutation.isPending}
        getUserDisplayName={getUserDisplayName}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        getUserDisplayName={getUserDisplayName}
        getStatusBadge={getStatusBadge}
        onActivate={(userId) => {
          setShowEditModal(false);
          handleApprovalRequest("activate", selectedUser);
        }}
        onDeactivate={(userId) => {
          setShowEditModal(false);
          handleApprovalRequest("deactivate", selectedUser);
        }}
        isActivating={false}
        isDeactivating={false}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        getUserDisplayName={getUserDisplayName}
        getStatusBadge={getStatusBadge}
        getDocumentDisplayName={getDocumentDisplayName}
      />

      {/* Review Registration Details Modal */}
      <ReviewRegistrationModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        getUserDisplayName={getUserDisplayName}
        getDocumentDisplayName={getDocumentDisplayName}
        onApprove={() => handleUserAction("approve", selectedUser)}
        onReject={() => handleUserAction("reject", selectedUser)}
      />

      {/* Approval Request Modal */}
      <ApprovalRequestModal
        isOpen={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedUserForApproval(null);
          setApprovalActionType("");
        }}
        actionType={approvalActionType}
        selectedUser={selectedUserForApproval}
        onSuccess={handleApprovalRequestSuccess}
      />
    </div>
  );
};

export default UserManagement;
